import type { ArticleSimpleTeaser, FeedTeaser, Teaser, TeaserHero } from '#domains/resolver/board-section-data';

import { BoardSectionItemFeedItemTeaser } from './Items/BoardSectionFeedItem';
import { BoardSectionItemArticle } from './Items/BoardSectionItemArticle';
import { BoardSectionItemArticleHighlight } from './Items/BoardSectionItemArticleHighlight';
import { BoardSectionItemBoard } from './Items/BoardSectionItemBoard';
import { BoardSectionItemBoardHighlight } from './Items/BoardSectionItemBoardHighlight';
import { BoardSectionItemEmbed } from './Items/BoardSectionItemEmbed';
import { BoardSectionItemTeaser } from './Items/BoardSectionItemTeaser';
import { BoardSectionItemTeaserHighlight } from './Items/BoardSectionItemTeaserHighlight';

/**
 * Feed items do not have images, therefore `sizes` is only required when `isFeedItem` is a falsy value.
 *
 * Feed items and highlight sections are exclusive.
 */
type FeedTypeProps =
	| {
			teaser: FeedTeaser;
			isFeedItem: true;
			isHighlightSection?: false;
	  }
	| {
			teaser: Teaser | TeaserHero;
			isFeedItem?: false;
			isHighlightSection?: true;
			sizes: string;
	  };

type BoardSectionItemType = FeedTypeProps;

type Props = {
	isLarge?: boolean;
	isPriority?: boolean;
	className?: string;
} & BoardSectionItemType;

export function BoardSectionItem({ isLarge, isPriority, className = '', ...rest }: Props) {
	if (rest.isFeedItem) {
		return <BoardSectionItemFeedItemTeaser className={className} teaser={rest.teaser} />;
	}

	if (rest.teaser.type === 'article') {
		if (rest.isHighlightSection) {
			return <BoardSectionItemArticleHighlight className={className} teaser={rest.teaser as ArticleSimpleTeaser} sizes={rest.sizes} />;
		}

		return (
			<BoardSectionItemArticle className={className} teaser={rest.teaser} isLarge={isLarge} isPriority={isPriority} sizes={rest.sizes} />
		);
	}

	if (rest.teaser.type === 'board') {
		if (rest.isHighlightSection) {
			return <BoardSectionItemBoardHighlight teaser={rest.teaser} sizes={rest.sizes} />;
		}

		return <BoardSectionItemBoard teaser={rest.teaser} isLarge={isLarge} sizes={rest.sizes} />;
	}

	if (rest.teaser.type === 'link') {
		if (rest.isHighlightSection) {
			return <BoardSectionItemTeaserHighlight teaser={rest.teaser} sizes={rest.sizes} />;
		}

		return <BoardSectionItemTeaser teaser={rest.teaser} isLarge={isLarge} sizes={rest.sizes} />;
	}

	if (rest.teaser.type === 'embed') {
		return <BoardSectionItemEmbed teaser={rest.teaser} isLarge={isLarge} />;
	}

	return null;
}
